// Small, dependency-free helpers around the Performance API and cooperative
// scheduling on the main thread.
//
// Everything here degrades gracefully: when `performance` or a particular API
// is missing (old browsers, tests without a DOM) the helpers still return
// durations, they just stop publishing entries to the timeline.

const MEASURE_PREFIX = 'kop:'

// In-memory copy of the last measurements so the app can read them without
// walking the performance timeline (which can be cleared by devtools or by
// exceeding the entry buffer).
const recorded = []
const MAX_RECORDED = 200

const hasPerformance = typeof performance !== 'undefined' && typeof performance.now === 'function'

export const now = () => (hasPerformance ? performance.now() : Date.now())

export const mark = (name, detail) => {
  if (!hasPerformance || typeof performance.mark !== 'function') return
  try {
    performance.mark(`${MEASURE_PREFIX}${name}`, detail !== undefined ? { detail } : undefined)
  } catch (e) {
    // Older implementations reject the options object; the mark is optional.
    try {
      performance.mark(`${MEASURE_PREFIX}${name}`)
    } catch (ignored) {
      /* timeline unavailable */
    }
  }
}

/**
 * Records a duration measured between two `performance.now()` timestamps and,
 * where supported, publishes it as a User Timing entry so it shows up in
 * browser performance traces next to long tasks and GC.
 *
 * @returns {{ name: string, duration: number, detail: any }}
 */
export const record = (name, startTime, endTime = now(), detail = undefined) => {
  const duration = Math.max(0, endTime - startTime)
  const entry = { name, duration, detail, at: Date.now() }

  recorded.push(entry)
  if (recorded.length > MAX_RECORDED) recorded.splice(0, recorded.length - MAX_RECORDED)

  if (hasPerformance && typeof performance.measure === 'function') {
    try {
      performance.measure(`${MEASURE_PREFIX}${name}`, { start: startTime, end: endTime, detail })
    } catch (e) {
      // performance.measure(name, options) is unsupported in some engines and
      // performance.measure(name) alone would measure from navigation start,
      // which is misleading. Skip the timeline entry in that case.
    }
  }

  return entry
}

/**
 * Convenience: `const stop = startTimer('decode'); ...; stop({ bytes })`
 * returns the recorded entry.
 */
export const startTimer = (name) => {
  const startTime = now()
  return (detail) => record(name, startTime, now(), detail)
}

export const getRecorded = () => recorded.slice()

export const clearRecorded = () => {
  recorded.length = 0
}

/**
 * Yields to the browser so it can handle input and paint between chunks of
 * CPU work. `requestAnimationFrame` alone is not enough: the callback runs
 * before paint in the same frame and can keep a long task going.
 *
 * Prefers `scheduler.yield()` (continues with high priority after the browser
 * had its turn) and falls back to a macrotask, which is what actually lets a
 * pending frame render.
 */
export const yieldToMain = () => {
  if (typeof globalThis.scheduler !== 'undefined' && typeof globalThis.scheduler.yield === 'function') {
    try {
      return globalThis.scheduler.yield()
    } catch (e) {
      /* fall through */
    }
  }
  return new Promise((resolve) => setTimeout(resolve, 0))
}

export const DEFAULT_CHUNK_BUDGET_MS = 8
export const DEFAULT_MAX_CHUNK = 2048

/**
 * Runs `fn(item, index)` for every item while keeping each synchronous slice
 * within `budgetMs`, then yields to the main thread. The check happens every
 * `checkEvery` items so the clock is not read for every record, and a chunk
 * never exceeds `maxChunk` items even on fast machines, which bounds the worst
 * case when a slow machine is not the problem but a stuck clock is.
 *
 * `shouldAbort()` lets a caller stop early (for example when the result has
 * been superseded); the loop then resolves with `aborted: true`.
 *
 * @returns {Promise<{ processed: number, chunks: number, duration: number, aborted: boolean }>}
 */
export const processInChunks = async (items, fn, options = {}) => {
  const budgetMs = options.budgetMs ?? DEFAULT_CHUNK_BUDGET_MS
  const maxChunk = options.maxChunk ?? DEFAULT_MAX_CHUNK
  const checkEvery = options.checkEvery ?? 32
  const shouldAbort = options.shouldAbort
  const length = items ? items.length : 0

  const startTime = now()
  let chunks = 0
  let index = 0

  while (index < length) {
    if (shouldAbort && shouldAbort()) {
      return { processed: index, chunks, duration: now() - startTime, aborted: true }
    }

    const chunkStart = now()
    const chunkLimit = Math.min(length, index + maxChunk)
    let processedInChunk = 0

    while (index < chunkLimit) {
      fn(items[index], index)
      index++
      processedInChunk++
      if (processedInChunk % checkEvery === 0 && now() - chunkStart >= budgetMs) break
    }

    chunks++
    if (index < length) {
      await yieldToMain()
    }
  }

  return { processed: index, chunks, duration: now() - startTime, aborted: false }
}

export default {
  now,
  mark,
  record,
  startTimer,
  getRecorded,
  clearRecorded,
  yieldToMain,
  processInChunks,
}
